import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Btn } from "@/components/common";
import { loadSpeedboat, useSpeedboat } from "@/lib/speedboat";
import { Ship, ChevronDown } from "lucide-react";
import SpeedboatAlign from "@/components/SpeedboatAlign";

const SEASONS = [["alta", "Alta stagione"], ["bassa", "Bassa stagione"]];
const DAYS = [["feriale", "Sabato – Giovedì"], ["venerdi", "Venerdì"]];
const ROUTES = [["arrivi", "Malé → Keyodhoo (arrivo)"], ["partenze", "Keyodhoo → Malé (partenza)"]];
const SLOTS = 4;
const pad = (a) => [...(a || []), ...Array(SLOTS).fill("")].slice(0, SLOTS);
const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const sel = "px-2 py-1 rounded border border-slate-300 bg-white";

function DayMonth({ value, onChange, testid }) {
  const [m, d] = value.split("-");
  const two = (n) => String(n).padStart(2, "0");
  return (
    <span className="inline-flex gap-1">
      <select data-testid={`${testid}-day`} value={d} onChange={(e) => onChange(`${m}-${e.target.value}`)} className={sel}>
        {Array.from({ length: 31 }, (_, i) => <option key={i} value={two(i + 1)}>{i + 1}</option>)}
      </select>
      <select data-testid={`${testid}-month`} value={m} onChange={(e) => onChange(`${e.target.value}-${d}`)} className={sel}>
        {MESI.map((x, i) => <option key={x} value={two(i + 1)}>{x}</option>)}
      </select>
    </span>
  );
}

function DayBlock({ f, season, day, label, set }) {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-xs font-semibold text-slate-700 mb-2">{label}</div>
      {ROUTES.map(([k, l]) => (
        <div key={k} className="mb-2">
          <div className="text-[11px] text-slate-500 mb-1">{l}</div>
          <div className="flex flex-wrap gap-1.5">
            {pad(f[season][day][k]).map((v, i) => (
              <input key={i} type="text" inputMode="numeric" maxLength={5} placeholder="--:--" value={v} data-testid={`sb-${season}-${day}-${k}-${i}`}
                onChange={(e) => set(season, day, k, i, e.target.value.replace(/[^\d:]/g, ""))}
                className="w-[72px] px-2 py-1 text-sm text-center tabular rounded border border-slate-300" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SpeedboatSchedule() {
  const s = useSpeedboat();
  const [f, setF] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (s) setF(JSON.parse(JSON.stringify(s))); }, [s]);
  if (!f) return null;
  const set = (season, day, k, i, v) => {
    const n = JSON.parse(JSON.stringify(f));
    const arr = pad(n[season][day][k]);
    arr[i] = v;
    n[season][day][k] = arr;
    setF(n);
  };
  const save = async () => {
    try {
      await api.put("/speedboat-schedule", f);
      await loadSpeedboat(true);
      toast.success("Orari speedboat salvati");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="rounded-lg border border-sky-200 bg-white mb-5 no-print" data-testid="sb-schedule">
      <button data-testid="sb-schedule-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <Ship size={16} className="text-sky-700" />
        <span className="text-sm font-semibold text-slate-800">Orari speedboat</span>
        <span className="text-[11px] text-slate-400">solo admin</span>
        <ChevronDown size={16} className={`ml-auto text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap items-center gap-2 text-sm mb-3">
            <span className="font-medium text-slate-700">Alta stagione dal</span>
            <DayMonth testid="sb-alta-from" value={f.alta_from} onChange={(v) => setF({ ...f, alta_from: v })} />
            <span className="font-medium text-slate-700">al</span>
            <DayMonth testid="sb-alta-to" value={f.alta_to} onChange={(v) => setF({ ...f, alta_to: v })} />
            <span className="text-[11px] text-slate-400">ogni anno (conta solo giorno e mese) · il resto è bassa stagione</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            {SEASONS.map(([season, sl]) => (
              <div key={season}>
                <div className={`text-xs font-bold uppercase tracking-wide mb-2 ${season === "alta" ? "text-orange-600" : "text-slate-500"}`}>{sl}</div>
                <div className="space-y-2">
                  {DAYS.map(([day, dl]) => <DayBlock key={day} f={f} season={season} day={day} label={dl} set={set} />)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 mt-3">
            <p className="text-[11px] text-slate-400">Formato 24h (es. 05:45). Campo vuoto = nessuna barca. Le prenotazioni già salvate mantengono il loro orario.</p>
            <Btn data-testid="sb-save" onClick={save}>Salva orari</Btn>
          </div>
          <SpeedboatAlign />
        </div>
      )}
    </div>
  );
}
